import React, { useState, useEffect, useRef } from 'react';
import { HashRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import MultiStepForm from './components/Form/MultiStepForm';
import CustomerPortal from './components/Portal/CustomerPortal';
import CustomerAccount from './components/Portal/CustomerAccount';
import AdminPanel from './components/Admin/AdminPanel';
import AdminGate from './components/Auth/AdminGate';
import PortalGate from './components/Auth/PortalGate';
import OrderingGate from './components/Auth/OrderingGate';
import CorporateGate from './components/Corporate/CorporateGate';
import CorporatePage from './components/Corporate/CorporatePage';
import PaymentLanding from './components/Corporate/PaymentLanding';
import SafeIcon from './common/SafeIcon';
import { supabase } from './lib/supabase';

const LOGO_URL = "/images/pkab-horizsm.png";

// ─── Install Prompt Hook ───────────────────────────────────────────────────
function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);

    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setInstallPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const triggerInstall = async () => {
    if (isIOS) { setShowIOSInstructions(true); return; }
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setIsInstalled(true); setInstallPrompt(null); }
  };

  const canInstall = !isInstalled && (installPrompt !== null || isIOS);
  return { canInstall, isInstalled, isIOS, triggerInstall, showIOSInstructions, setShowIOSInstructions };
}

// ─── iOS Instructions Modal ────────────────────────────────────────────────
function IOSInstallModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center px-4 pb-4" onClick={onClose}>
      <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-5" onClick={e => e.stopPropagation()}>
        <div className="text-center space-y-2">
          <img src={LOGO_URL} alt="Phoenix Kabob" className="h-12 w-auto mx-auto" />
          <h3 className="text-xl font-serif text-charcoal">Add to Home Screen</h3>
          <p className="text-sm text-sage-500">Install Phoenix Kabob for a better experience.</p>
        </div>
        <ol className="space-y-4">
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-blush-100 rounded-full flex items-center justify-center text-blush-500 font-bold text-sm shrink-0 mt-0.5">1</div>
            <div className="text-sm text-charcoal">Tap the <strong>Share</strong> button at the bottom of your browser <span className="ml-1 inline-flex items-center justify-center w-6 h-6 bg-sage-100 rounded text-sage-500 text-xs">⬆</span></div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-blush-100 rounded-full flex items-center justify-center text-blush-500 font-bold text-sm shrink-0 mt-0.5">2</div>
            <p className="text-sm text-charcoal">Scroll down and tap <strong>"Add to Home Screen"</strong></p>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-blush-100 rounded-full flex items-center justify-center text-blush-500 font-bold text-sm shrink-0 mt-0.5">3</div>
            <p className="text-sm text-charcoal">Tap <strong>"Add"</strong> to confirm</p>
          </li>
        </ol>
        <button onClick={onClose} className="btn-secondary w-full">Got it</button>
      </div>
    </div>
  );
}

// ─── Mobile Bottom Nav ─────────────────────────────────────────────────────
function MobileBottomNav({ session, onSignOut, installProps }) {
  const location = useLocation();
  const { canInstall, triggerInstall } = installProps;
  const isPortal = location.pathname === '/portal';
  const isHome = location.pathname === '/';
  const isAccount = location.pathname === '/account';

  return (
    <nav className="bottom-nav fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-sage-100 flex items-center justify-around px-2 pt-3 md:hidden shadow-[0_-4px_20px_-2px_rgba(0,0,0,0.06)]">

      {/* Order */}
      <Link
        to="/"
        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all ${isHome ? 'text-blush-400' : 'text-sage-400'}`}
      >
        <SafeIcon name="Flame" className="text-2xl" />
        <span className="text-[10px] font-bold uppercase tracking-wider">Order</span>
      </Link>

      {/* My Orders */}
      <Link
        to="/portal"
        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all ${isPortal ? 'text-blush-400' : 'text-sage-400'}`}
      >
        <SafeIcon name="Package" className="text-2xl" />
        <span className="text-[10px] font-bold uppercase tracking-wider">My Orders</span>
      </Link>

      {/* My Account — routes to account page if logged in, portal (login) if not */}
      <Link
        to={session ? '/account' : '/portal'}
        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all ${isAccount ? 'text-blush-400' : 'text-sage-400'}`}
      >
        <SafeIcon name="User" className="text-2xl" />
        <span className="text-[10px] font-bold uppercase tracking-wider">Account</span>
      </Link>

      {/* Install — only shows when installable */}
      {canInstall && (
        <button
          onClick={triggerInstall}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-2xl text-sage-400 transition-all"
        >
          <SafeIcon name="Download" className="text-2xl" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Install</span>
        </button>
      )}

      {/* Sign Out — only when logged in and install not showing */}
      {session && !canInstall && (
        <button
          onClick={onSignOut}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-2xl text-sage-400 transition-all active:text-red-400"
        >
          <SafeIcon name="LogOut" className="text-2xl" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Sign Out</span>
        </button>
      )}
    </nav>
  );
}

// ─── Set Password Overlay (coordinator invite flow) ───────────────────────
function SetPasswordOverlay({ onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) { setError(updateError.message); setLoading(false); return; }
    onSuccess();
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center px-4 bg-charcoal/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <img src={LOGO_URL} alt="Phoenix Kabob" className="h-10 w-auto mx-auto" />
          <h2 className="text-2xl font-serif text-charcoal">Set your password</h2>
          <p className="text-sm text-sage-500">Choose a password to activate your coordinator account.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            placeholder="New password (min 8 characters)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={8}
            className="input-field w-full"
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
            className="input-field w-full"
          />
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <button type="submit" disabled={loading} className="btn-primary w-full min-h-[48px]">
            {loading ? 'Saving…' : 'Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────────
function Layout({ children, isEmbed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Detect recovery mode immediately (before effects) by checking the URL hash
  const [recoveryMode, setRecoveryMode] = useState(
    () => window.location.hash.includes('type=recovery')
  );
  const isAdminRoute = location.pathname === '/admin';
  const hideChrome = isEmbed || location.pathname === '/';
  const installProps = useInstallPrompt();

  const checkAdminStatus = async (sess) => {
    const { data } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', sess.user.id)
      .single();
    setIsAdmin(data?.is_admin || false);
  };

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      if (session) checkAdminStatus(session);
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
      }
      if (sess) checkAdminStatus(sess);
      else setIsAdmin(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handlePasswordSet = async () => {
    setRecoveryMode(false);
    // After coordinator sets their password, navigate them to their company page
    const { data: { session: currentSess } } = await supabase.auth.getSession();
    if (!currentSess) return;
    const { data: profile } = await supabase
      .from('profiles')
      .select('corporate_client_id, corporate_clients(slug)')
      .eq('id', currentSess.user.id)
      .single();
    const slug = profile?.corporate_clients?.slug;
    if (slug) navigate(`/c/${slug}`);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Admin route — clean desktop layout only
  if (isAdminRoute) {
    return (
      <div className="min-h-screen flex flex-col w-full">
        {recoveryMode && <SetPasswordOverlay onSuccess={handlePasswordSet} />}
        <header className="bg-white border-b border-sage-100 py-4 px-4 md:px-8 lg:px-12 flex justify-between items-center sticky top-0 z-50 w-full">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Phoenix Kabob" className="h-12 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-sm font-medium text-sage-600">
            <Link to="/portal" className="hover:text-blush-400 flex items-center gap-2">
              <SafeIcon name="User" /> <span className="hidden sm:inline">Portal</span>
            </Link>
            <button onClick={handleSignOut} className="ml-2 bg-sage-50 hover:bg-sage-100 text-sage-500 p-2 rounded-full transition-colors" title="Sign Out">
              <SafeIcon name="LogOut" />
            </button>
          </nav>
        </header>
        <main className="flex-1 flex flex-col w-full">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col w-full">
      {recoveryMode && <SetPasswordOverlay onSuccess={handlePasswordSet} />}
      {/* Desktop header */}
      {!hideChrome && (
        <header className="hidden md:flex bg-white border-b border-sage-100 py-4 px-4 md:px-8 lg:px-12 justify-between items-center sticky top-0 z-50 w-full">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Phoenix Kabob" className="h-12 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 md:gap-6 text-sm font-medium text-sage-600">
            <Link to="/portal" className="hover:text-blush-400 flex items-center gap-2">
              <SafeIcon name="Package" /> <span>My Orders</span>
            </Link>
            <Link to={session ? '/account' : '/portal'} className="hover:text-blush-400 flex items-center gap-2">
              <SafeIcon name="User" /> <span>My Account</span>
            </Link>
            {isAdmin && (
              <Link to="/admin" className="hover:text-blush-400 flex items-center gap-2">
                <SafeIcon name="Settings" /> <span>Admin</span>
              </Link>
            )}
            {installProps.canInstall && (
              <button onClick={installProps.triggerInstall} className="flex items-center gap-2 text-blush-400 font-semibold hover:text-blush-500 transition-colors">
                <SafeIcon name="Download" /> Add to Home Screen
              </button>
            )}
            <button onClick={handleSignOut} className="ml-2 bg-sage-50 hover:bg-sage-100 text-sage-500 p-2 rounded-full transition-colors" title="Sign Out">
              <SafeIcon name="LogOut" />
            </button>
          </nav>
        </header>
      )}

      {/* Mobile header */}
      {!hideChrome && (
        <header className="md:hidden bg-white border-b border-sage-100 py-3 px-4 flex justify-between items-center sticky top-0 z-50 w-full">
          <Link to="/">
            <img src={LOGO_URL} alt="Phoenix Kabob" className="h-10 w-auto" />
          </Link>
          {isAdmin && (
            <Link to="/admin" className="text-sage-400 p-2">
              <SafeIcon name="Settings" className="text-xl" />
            </Link>
          )}
        </header>
      )}

      <main className={`flex-1 flex flex-col w-full ${!hideChrome ? 'has-bottom-nav md:pb-0' : ''}`}>
        {children}
      </main>

      {/* Desktop footer */}
      {!hideChrome && (
        <footer className="hidden md:block bg-sage-50 border-t border-sage-100 py-8 text-center text-sm text-sage-500 mt-auto w-full">
          <img src={LOGO_URL} alt="Phoenix Kabob" className="h-8 w-auto mx-auto mb-4 opacity-50 grayscale" />
          <p>&copy;{new Date().getFullYear()} Phoenix Kabob. All rights reserved.</p>
        </footer>
      )}

      {/* Mobile bottom nav */}
      {!hideChrome && (
        <MobileBottomNav
          session={session}
          onSignOut={handleSignOut}
          installProps={installProps}
        />
      )}

      {installProps.showIOSInstructions && (
        <IOSInstallModal onClose={() => installProps.setShowIOSInstructions(false)} />
      )}
    </div>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────
export default function App({ isEmbed = false, primaryColor }) {
  return (
    <HashRouter>
      <Layout isEmbed={isEmbed}>
        <Routes>
          <Route path="/" element={<OrderingGate><MultiStepForm primaryColor={primaryColor} /></OrderingGate>} />
          <Route path="/portal" element={<PortalGate><CustomerPortal /></PortalGate>} />
          <Route path="/account" element={<PortalGate><CustomerAccount /></PortalGate>} />
          <Route path="/admin" element={<AdminGate><AdminPanel /></AdminGate>} />
          <Route path="/c/:slug" element={<CorporateGate><CorporatePage /></CorporateGate>} />
          <Route path="/pay/:itemId" element={<PaymentLanding />} />
        </Routes>
      </Layout>
    </HashRouter>
  );
}
